<template>
  <div>
    <button-filter
      :btnClass="{
        'button-filter--open': $refs.popover ? $refs.popover.show : false,
      }"
      :iconTune="$refs.popover ? !$refs.popover.show : true"
      :title="title"
      @click="toggle = !toggle"
    />
    <Popover :toggle="toggle" ref="popover" class="popover-tickets-filter">
      <div class="filters">
        <div
          class="filter-wrap"
          v-for="filter in filtersToShow"
          :key="filter._id"
        >
          <label>{{ filter.label }}</label>
          <div v-if="filter._id.includes('Date')">
            <b-form-input
              class="from-to-date"
              type="date"
              v-model="filter.selected"
            />
          </div>
          <Dropdown
            v-else
            :data="filter.items"
            :showSeeAll="false"
            :showSearch="filter.showSearch"
            :selectedId="filter.selected._id"
            @selectItem="data => itemSelect(filter._id, data)"
          />
        </div>
      </div>

      <template slot="footer">
        <div class="footer-wrap">
          <b-link variant="primary" @click="resetFilters">
            {{ labels.clear }}
          </b-link>
          <b-button
            class="apply-button ml-4"
            variant="primary"
            @click="applyFilters"
          >
            {{ labels.apply }}
          </b-button>
        </div>
      </template>
    </Popover>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Popover from '@/components/Shared/Popover.vue'
import Dropdown from '@/components/Shared/Form/Dropdown.vue'
import ButtonFilter from '@/components/Shared/ButtonFilter.vue'
import { loadAvailableFilters } from '@/service/tickets'
import { isBasicPlan } from '@/service/user'

export default {
  components: {
    Popover,
    Dropdown,
    ButtonFilter,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    filtersByOwner: {
      type: Boolean,
      default: true,
    },
    customSurvey: {
      type: String,
    },
    selectedGroupId: {
      type: String,
    },
    selectedQuestionId: {
      type: String,
    },
  },
  data() {
    return {
      filters: [],
      toggle: false,
    }
  },
  mounted() {
    const customSurvey = this.customSurvey
    loadAvailableFilters({ customSurvey }).then(this.setFilters)
  },
  computed: {
    ...mapGetters(['resources']),
    labels() {
      return Object.assign(
        {},
        this.resources.general.buttons,
        this.resources.general.badge,
        this.resources.component.dropdownGroups
      )
    },
    filterDefaultValue() {
      return this.labels.seeAll
    },
    filterDefaultOption() {
      return {
        _id: this.filterDefaultValue,
        name: this.filterDefaultValue,
        enable: true,
      }
    },
    selectedFilters() {
      return Object.values(this.filters).filter(
        ({ selected }) => selected && selected._id !== this.filterDefaultValue
      )
    },
    filtersToShow() {
      const filtersByMyTickets = ['status', 'feeling', 'closed', 'rated']
      const available = ({ _id }) => filtersByMyTickets.includes(_id)

      return this.filtersByOwner
        ? Object.values(this.filters).filter(available)
        : Object.values(this.filters)
    },
  },
  methods: {
    normalizeDropdownData(filter) {
      const badgeProps = {
        badge: {
          show: true,
          text: this.labels.enterpriseText,
          classes: 'badge-enterprise badge-danger badge-default',
        },
        enable: false,
      }

      const applyEnterpriseBadge = item => ({ ...item, ...badgeProps })

      const filtersSource = items =>
        Array.from(
          items
            .reduce(
              (map, cur) => map.set(cur._id, { ...cur, enable: true }),
              new Map().set(this.filterDefaultValue, this.filterDefaultOption)
            )
            .values()
        )

      const getFilter = data => {
        if (isBasicPlan() && ['group'].includes(data._id)) {
          data.items = data.items.map(applyEnterpriseBadge)
        }

        const getShowSearchOnDropdown = _id => _id === 'group'

        const selected = data._id.includes('Date')
          ? null
          : data.selected || this.filterDefaultOption

        return {
          _id: data._id,
          label: data.label,
          selected,
          showSearch: getShowSearchOnDropdown(data._id),
          items: {
            Filters: filtersSource(data.items),
          },
        }
      }

      return getFilter(filter)
    },

    format(data) {
      const normalizeFilters = filters =>
        filters
          .filter(i => i._id)
          .reduce(
            (acc, filter) =>
              Object.assign(acc, {
                [filter._id]: this.normalizeDropdownData(filter),
              }),
            {}
          )

      return normalizeFilters(data)
    },
    setFilters(filters) {
      const formatedFilters = this.format(filters)

      Object.keys(formatedFilters).forEach(key => {
        if (key.includes('Date')) {
          formatedFilters[key].selected = null
        }
      })

      this.filters = formatedFilters

      const selectedGroupId = this.selectedGroupId
        ? this.selectedGroupId
        : 'consolidate'

      if (selectedGroupId !== 'consolidate') {
        let selected = this.filters['group'].items.Filters.find(
          i => i._id === selectedGroupId
        )
        this.filters['group'].selected = selected
      }

      const selectedQuestionId = this.selectedQuestionId
      if (selectedQuestionId) {
        let selected = this.filters['question'].items.Filters.find(
          i => i._id === selectedQuestionId
        )
        this.filters['question'].selected = selected
      }
    },
    itemSelect(filterId, item) {
      this.filters[filterId].selected = item
    },
    resetFilters() {
      Object.keys(this.filters).forEach(key => {
        if (key.includes('Date')) {
          this.filters[key].selected = null
        } else {
          this.filters[key].selected = this.filterDefaultOption
        }
      })
      this.applyFilters()
    },
    applyFilters() {
      this.validateEnterpriseException()
      this.$emit('applyFilters', this.selectedFilters)
      this.toggle = false
    },
    validateEnterpriseException() {
      if (!isBasicPlan()) return
      const hasBadgedFilters = filters =>
        filters.filter(({ selected }) => selected.badge)

      const withBadge = hasBadgedFilters(this.selectedFilters)

      if (withBadge.length) {
        this.$emit('filterWithBadgeSelected')
        withBadge.map(
          item => (this.filters[item._id].selected = this.filterDefaultOption)
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';
@import '~bootstrap/scss/_functions';
@import '~bootstrap/scss/_variables';
@import '~bootstrap/scss/mixins/breakpoints';

.button-filter--open {
  z-index: 1000;
}

.footer-wrap {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 1rem;
}

/deep/ .icon-tune:before {
  display: flex;
}

.apply-button {
  width: 50%;
  max-width: 200px;
}

::v-deep .tc-popover {
  width: 80%;
  min-width: 200px;
  margin-left: auto;
  margin-right: 5em;

  @include media-breakpoint-up(sm) {
    max-width: 500px;
    width: 100vw;
  }

  @include media-breakpoint-up(lg) {
    margin: 10px;
  }
}

.filters {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  justify-content: space-around;

  .filter-wrap {
    align-items: left;
    border-top: 1px solid map-get($theme-colors, 'gray01');
    display: flex;
    flex-basis: 50%;
    flex-direction: column;
    margin-bottom: 1rem;
    max-width: 100%;
    padding-top: 0.8rem;
    width: 100vw;

    &:first-child {
      border-top: none;
      padding-top: 0;
    }

    label {
      font-size: 12px;
      width: 100%;
    }

    .b-dropdown {
      width: 100%;
    }

    @media (max-width: '768px') {
      border-top: none;

      &:first-child {
        padding-top: 0.8rem;
      }

      &:nth-child(3),
      &:nth-child(4) {
        border-top: 1px solid map-get($theme-colors, 'gray01');
        border-bottom: 1px solid map-get($theme-colors, 'gray01');
        margin: 0;
        padding: 1rem 0;
      }

      label {
        font-size: 12px;
      }
    }

    @media (max-width: '425px') {
      border-top: 1px solid map-get($theme-colors, 'gray01');

      &:nth-child(3),
      &:nth-child(4) {
        border-bottom: none;
        margin: inherit;
        padding: inherit;
        padding-top: 0.8rem;
        margin-bottom: 1rem;
      }
    }

    .from-to-date {
      background-color: map-get($theme-colors, 'white') !important;
      border-color: var(--gray07) !important;
      height: 48px;
    }
  }

  @media (max-width: 425px) {
    flex-direction: column;
    flex-wrap: nowrap;

    .filter-wrap {
      width: 98%;
    }
  }
}
</style>
